import { useEffect, useRef } from 'react';

/**
 * Accessibility behavior for hand-built modal dialogs:
 *  - Escape closes the dialog (calls onClose)
 *  - Focus is trapped inside the dialog (Tab / Shift+Tab cycle within it)
 *  - Focus moves into the dialog on open and returns to the opener on close
 *
 * Usage:
 *   const dialogRef = useDialogA11y(isOpen, onClose);
 *   return (
 *     <div role="dialog" aria-modal="true" aria-label="...">
 *       <div ref={dialogRef} className="modal-panel"> ...content... </div>
 *     </div>
 *   );
 * Attach the ref to the focusable panel (the box that holds the inputs).
 */
export function useDialogA11y(isOpen, onClose) {
  const containerRef = useRef(null);
  const previouslyFocused = useRef(null);
  // Keep the latest onClose in a ref so the effect does NOT depend on its
  // identity. Callers pass inline arrows that change every render; if the
  // effect re-ran per keystroke it would repeatedly yank focus to the first
  // input (the "can only type one character" bug).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocused.current = document.activeElement;
    const node = containerRef.current;

    const getFocusable = () =>
      node
        ? Array.from(
            node.querySelectorAll(
              'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => el.offsetParent !== null)
        : [];

    // Move focus into the dialog so keyboard/screen-reader users land inside it.
    const initial = getFocusable();
    (initial[0] || node)?.focus?.();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key === 'Tab') {
        const items = getFocusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      try { previouslyFocused.current?.focus?.(); } catch (_) {}
    };
    // Depend ONLY on isOpen — see onCloseRef note above.
  }, [isOpen]);

  return containerRef;
}
